import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { CTA_KEY } from '../../../components/CTA/constants';
import {
  CMSMeta,
  GoldSilverPageDataProps,
} from '../../../components/gold-silver-page/common/types';
import GoldPageLayout from '../../../components/gold-silver-page/layout/gold-page';
import SilverPageLayout from '../../../components/gold-silver-page/layout/silver-page';
import {
  flattenCityPaths,
  generateGoldSilverPage,
} from '../../../components/gold-silver-page/util';
import CMSPageDataServices from '../../../components/cms/services/cms-pages.service';
import { processHtml } from '../../../utils/static-pages/staticPages';
import { getCachedCTAData } from '../../../utils/cta-cache';

const { publicRuntimeConfig } = getConfig();

interface GeneralPathProps {
  pageData: GoldSilverPageDataProps;
  pageType: 'goldPage' | 'silverPage';
  top_cta: string;
  cmsContent: string;
  cmsMeta: CMSMeta;
}

export default function GeneralPath({
  pageData,
  pageType,
  top_cta,
  cmsContent,
  cmsMeta,
}: GeneralPathProps) {
  const router = useRouter();

  if (router.isFallback) {
    return <div>Loading...</div>;
  }
  switch (pageType) {
    case 'goldPage':
      return (
        <GoldPageLayout
          {...pageData.props}
          top_cta={top_cta}
          cmsContent={cmsContent}
          cmsMeta={cmsMeta}
        />
      );
    case 'silverPage':
      return (
        <SilverPageLayout
          {...pageData.props}
          top_cta={top_cta}
          cmsContent={cmsContent}
          cmsMeta={cmsMeta}
        />
      );
    default:
      return <></>;
  }
}

// Define static paths
export async function getStaticPaths() {
  if (process.env.DEPLOYMENT_DOMAIN !== 'cleartax.in') {
    return {
      paths: [],
      fallback: false,
    };
  }
  if (
    process.env.ENABLE_GOLD_SILVER_PAGES === 'true' &&
    !process.env.PAGE_URL
  ) {
    let goldPagePath = [],
      silerPagePath = [];
    try {
      const cityList = await fetch(
        `${publicRuntimeConfig.CLEARTAX_DOMAIN}/f/gold-silver/get-cities/`
      );
      const cityPath = await cityList.json();
      goldPagePath = flattenCityPaths(cityPath, 'gold-rate-');
      silerPagePath = flattenCityPaths(cityPath, 'silver-rate-');
    } catch (e) {
      return {
        paths: [],
        fallback: false,
      };
    }
    const goldPaths = goldPagePath?.map((gold_silver_path) => ({
      params: { gold_silver_path },
    }));
    const silverPaths = silerPagePath?.map((gold_silver_path) => ({
      params: { gold_silver_path },
    }));
    return {
      paths: [...goldPaths, ...silverPaths],
      fallback: false, // Change to true if adding more cities dynamically
    };
  }
  return {
    paths: [],
    fallback: false,
  };
}

// Provide props for each city
export async function getStaticProps({
  params,
}: {
  params: { gold_silver_path: string };
}) {
  const { gold_silver_path } = params;
  let goldPageData = {},
    silverPageData = {};
  if (gold_silver_path.startsWith('gold-rate')) {
    goldPageData =
      (await generateGoldSilverPage(gold_silver_path, 'gold-rate')) || {};
  } else {
    silverPageData =
      (await generateGoldSilverPage(gold_silver_path, 'silver-rate')) || {};
  }
  let cta;
  try {
    cta = await getCachedCTAData(CTA_KEY.GOLD_SILVER_CTA);
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    console.error('Error fetching CTA:', errorMessage);
  }

  let cmsContent = '';
  let cmsMeta = { meta_title: '', description: '', keywords: '', title: '' };
  try {
    const cmsResponse = await CMSPageDataServices.getPageByURL(
      gold_silver_path,
      1,
      1,
      'goldsilver'
    );
    if (cmsResponse.status === 200) {
      const cmsData = cmsResponse.data;
      const processedContent = await processHtml(cmsData?.content);
      cmsContent = processedContent || '';
      cmsMeta = {
        meta_title: cmsData.meta_title || '',
        description: cmsData.description || '',
        keywords: cmsData.keywords || '',
        title: cmsData.title || '',
      };
    }
  } catch (error: unknown) {
    console.error('Error fetching CMS data:', (error as Error)?.message);
  }

  return {
    props: {
      pageType: gold_silver_path.startsWith('gold-rate')
        ? 'goldPage'
        : 'silverPage',
      pageData:
        Object.keys(goldPageData).length || Object.keys(silverPageData).length
          ? { ...goldPageData, ...silverPageData }
          : null,
      top_cta: (cta as { cta_html?: string } | undefined)?.cta_html ?? '',
      cmsContent,
      cmsMeta,
    },
  };
}
